import { useState, useEffect, useMemo } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';

interface PresenceState {
  [userId: string]: {
    online: boolean;
    lastSeen?: number;
  };
}

export const usePresence = (studentIds: string[]) => {
  const [presence, setPresence] = useState<PresenceState>({});
  const [loading, setLoading] = useState(true);

  // Memoize studentIds to prevent infinite loops
  const studentIdsString = studentIds.join(',');
  const memoizedStudentIds = useMemo(() => studentIds, [studentIdsString]);

  useEffect(() => {
    if (memoizedStudentIds.length === 0) {
      setLoading(false);
      return;
    }

    // Set up presence for current user
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (user && user.uid) {
        // Validate user ID before using it in document path
        if (user.uid.includes('/') || user.uid.includes('//')) {
          console.warn('Invalid user ID for presence tracking:', user.uid);
          return;
        }
        
        const userPresenceRef = doc(db, 'presence', user.uid);
        
        // Set user as online - use setDoc with merge to create or update
        try {
          await setDoc(userPresenceRef, {
            online: true,
            lastSeen: serverTimestamp()
          }, { merge: true });
        } catch (error) {
          console.warn('Failed to set initial online status:', error);
          // If we can't set initial status, don't start the keep-alive interval
          if (error instanceof Error && error.message.includes('permission')) {
            console.warn('Permission denied for initial presence setup, skipping presence tracking');
            return;
          }
        }

        // Set up periodic updates to keep user online
        const keepAliveInterval = setInterval(async () => {
          try {
            // Check if user is still authenticated before updating
            if (auth.currentUser && auth.currentUser.uid === user.uid) {
              await setDoc(userPresenceRef, {
                lastSeen: serverTimestamp()
              }, { merge: true });
            } else {
              console.warn('User no longer authenticated, stopping keep-alive updates');
              clearInterval(keepAliveInterval);
            }
          } catch (error) {
            console.warn('Failed to update presence (keep alive):', error);
            // If we get a permission error, stop the interval
            if (error instanceof Error && error.message.includes('permission')) {
              console.warn('Permission denied for presence update, stopping keep-alive');
              clearInterval(keepAliveInterval);
            }
          }
        }, 30000); // Update every 30 seconds

        // Set user as offline when they disconnect (this is a best-effort approach)
        const handleBeforeUnload = async () => {
          try {
            await setDoc(userPresenceRef, {
              online: false,
              lastSeen: serverTimestamp()
            }, { merge: true });
          } catch (error) {
            console.warn('Failed to set offline status:', error);
          }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
          clearInterval(keepAliveInterval);
          window.removeEventListener('beforeunload', handleBeforeUnload);
          // Set offline on cleanup
          setDoc(userPresenceRef, {
            online: false,
            lastSeen: serverTimestamp()
          }, { merge: true }).catch((error) => {
            console.warn('Failed to set offline status on cleanup:', error);
          });
        };
      }
    });

    // Listen to presence changes for all students
    const unsubscribePresence = memoizedStudentIds.map(studentId => {
      // Validate student ID before using it in document path
      if (!studentId || studentId.includes('/') || studentId.includes('//')) {
        console.warn('Invalid student ID for presence tracking:', studentId);
        return () => {}; // Return empty unsubscribe function
      }
      
      const presenceRef = doc(db, 'presence', studentId);
      return onSnapshot(presenceRef, (snapshot) => {
        const data = snapshot.data();
        if (data) {
          const presenceData = {
            online: data.online || false,
            lastSeen: data.lastSeen?.toDate?.()?.getTime() || data.lastSeen
          };
          setPresence(prev => ({
            ...prev,
            [studentId]: presenceData
          }));
        } else {
          // Set as offline if document doesn't exist
          const presenceData = {
            online: false,
            lastSeen: undefined
          };
          setPresence(prev => ({
            ...prev,
            [studentId]: presenceData
          }));
        }
      });
    });

    setLoading(false);

    return () => {
      unsubscribeAuth();
      unsubscribePresence.forEach(unsubscribe => unsubscribe());
    };
  }, [memoizedStudentIds]);

  return { presence, loading };
};





